// One badge, for the admin: big art, the definition, rarity and who holds it
// (GET /api/badges/admin/badge-users/:code) with each holder's copy count.
// A bottom sheet on a phone, a dialog on a desktop, like the collection.
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { MobileSheet } from "../../../ui/components/MobileSheet";
import { Dialog, DialogContent, DialogTitle } from "../../../ui/components/Dialog";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { BadgeArt } from "../art";
import { familyLabel, fmtDate, fmtInt, pointsText, tierColor } from "../collection/badgeFormat";
import { useGetAdminBadgeHoldersQuery } from "./adminBadgesApi";
import { summarizeHolders, thresholdWords } from "./adminModel";
import type { AdminBadge } from "./adminTypes";
import { Markers, HoldersCell, TierChip } from "./AdminBits";

function Holders({ badge }: { badge: AdminBadge }) {
  const { data, isLoading, isFetching, isError, refetch } = useGetAdminBadgeHoldersQuery(badge.code);
  const holders = summarizeHolders(data, badge.id);
  const copies = holders.reduce((n, h) => n + h.copies, 0);

  let list: React.ReactNode;
  if (isLoading) {
    list = <p className="badm-muted">Loading who holds it</p>;
  } else if (isError) {
    list = (
      <div className="badm-inline-row">
        <p className="badm-muted">Could not load the holders.</p>
        <button type="button" className="badm-btn badm-btn--sm" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  } else if (!holders.length) {
    list = <p className="badm-muted">Nobody holds it yet.</p>;
  } else {
    list = (
      <ol className="badm-holders" aria-label={`Who holds ${badge.name}`}>
        {holders.map((h) => (
          <li key={h.userId} className="badm-holder" data-username={h.username}>
            <span className="badm-holder__top">
              <Link className="badm-holder__name" to={`/badges/u/${encodeURIComponent(h.username)}`}>
                {h.username}
              </Link>
              <span className="badm-holder__count" title="Copies">
                x{fmtInt(h.copies)}
              </span>
            </span>
            <span className="badm-holder__sub">
              <span className="badm-holder__date">
                {h.firstEarned ? fmtDate(h.firstEarned) : ""}
                {h.copies > 1 && h.lastEarned && fmtDate(h.lastEarned) !== fmtDate(h.firstEarned)
                  ? ` to ${fmtDate(h.lastEarned)}`
                  : ""}
              </span>
              {h.firstEver && <span className="badm-mark badm-mark--first">first on the site</span>}
              {h.historyCopies > 0 && (
                <span className="badm-mark">
                  {h.historyCopies === h.copies ? "history" : `${fmtInt(h.historyCopies)} history`}
                </span>
              )}
              {h.unseen > 0 && <span className="badm-mark badm-mark--unseen">{fmtInt(h.unseen)} not seen</span>}
            </span>
          </li>
        ))}
      </ol>
    );
  }

  return (
    <div className="badm-detail__holders">
      <h3 className="badm-detail__label">
        Who holds it
        {holders.length > 0 && (
          <span className="badm-detail__label-aside">
            {fmtInt(holders.length)} {holders.length === 1 ? "member" : "members"}
            {copies > holders.length ? `, ${fmtInt(copies)} copies` : ""}
            {isFetching && !isLoading ? ", refreshing" : ""}
          </span>
        )}
      </h3>
      {list}
    </div>
  );
}

export const BadgeAdminDetail: React.FC<{ badge: AdminBadge; titleAs?: React.ElementType }> = ({
  badge,
  titleAs: Title = "h2",
}) => {
  const isMobile = useMediaQuery("(max-width: 700px)");
  const s = badge.stats;
  const facts: Array<[string, React.ReactNode]> = [
    ["Tier", <TierChip key="t" tier={badge.tier} />],
    ["Points", pointsText(badge.points)],
    ["Threshold", thresholdWords(badge)],
    ["On the art", badge.number === null ? "no number" : fmtInt(badge.number)],
    ["Earned", badge.unique ? "by one member, ever" : badge.repeatable ? "more than once" : "once per member"],
    ["Status", badge.active ? "active" : "inactive, never awarded"],
  ];

  return (
    <div className="badm-detail" style={{ "--badm-rarity": tierColor(badge.tier) } as React.CSSProperties}>
      <div className="badm-detail__stage" data-tier={badge.tier}>
        <BadgeArt
          category={badge.category}
          tier={badge.tier}
          number={badge.number}
          code={badge.code}
          name={badge.name}
          size={isMobile ? 150 : 180}
          variant="full"
          animate={badge.active ? "idle" : "none"}
          locked={!badge.active}
        />
      </div>
      <div className="badm-detail__kicker">
        {familyLabel(badge.family)} · <code>{badge.category}</code>
      </div>
      <Title className="badm-detail__name">{badge.name}</Title>
      <code className="badm-code badm-detail__code">{badge.code}</code>
      {badge.description && <p className="badm-detail__desc">{badge.description}</p>}
      <div className="badm-detail__marks">
        <Markers badge={badge} />
      </div>

      <div className="badm-detail__rarity">
        <HoldersCell badge={badge} />
        {s && s.members > 0 && <span className="badm-muted">{s.pct}% of members with a badge</span>}
      </div>

      <dl className="badm-facts">
        {facts.map(([k, v]) => (
          <div key={k} className="badm-facts__row">
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
        {s?.firstEarnedAt && (
          <div className="badm-facts__row">
            <dt>First earned</dt>
            <dd>{fmtDate(s.firstEarnedAt)}</dd>
          </div>
        )}
      </dl>

      <Holders badge={badge} />
    </div>
  );
};

/** The detail in a MobileSheet (up to 700 px wide) or a dialog. */
const BadgeAdminDetailSheet: React.FC<{
  badge: AdminBadge | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ badge, open, onOpenChange }) => {
  const isMobile = useMediaQuery("(max-width: 700px)");
  // keep the last badge on screen while the sheet animates out
  const last = useRef<AdminBadge | null>(null);
  if (badge) last.current = badge;
  const shown = badge ?? last.current;
  const content = shown ? <BadgeAdminDetail badge={shown} titleAs={DialogTitle} /> : null;

  if (isMobile) {
    return (
      <MobileSheet
        open={open && !!shown}
        onOpenChange={onOpenChange}
        fitContent
        maxHeight="88vh"
        showCloseButton
        title={shown ? familyLabel(shown.family) : undefined}
        contentClassName="badm-sheet"
      >
        {content}
      </MobileSheet>
    );
  }
  return (
    <Dialog open={open && !!shown} onOpenChange={onOpenChange}>
      <DialogContent className="badm-dialog" aria-describedby={undefined}>
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default BadgeAdminDetailSheet;
